import {
  Body,
  Box,
  Button,
  Flex,
  Heading,
  Link,
  RawMoney,
} from '@edreamsodigeo/prisma-design-system';
import {
  createFeature,
  Desktop,
  Mobile,
  Trans,
  useTranslation,
} from '@edreamsodigeo/frontend-core';
import React from 'react';
import { PriceFreezeDate } from '@/components/PriceFreezeDateTime/PriceFreezeDateTime';
import { ChronologyContent, NumberChronology } from '@/components/Chronology';
import { ACTIONS, usePFTracking } from '@/hooks/tracking';
import { useIsWebView } from '@/hooks/useIsWebView';
import { useOpenAppDeepLink } from '@/components/ConfirmationStepSuccess/useLandingPage';
import { withDatameshClick } from '@/HOC/withDatamesh';
import { PRIME_FIRST_PHASE_2_MOBILE } from '@/versionedComponents';

export const TotalPriceStyle = ({ money = {} }) => (
  <Box alignSelf="flex-end">
    <Desktop>
      <Heading type="2" weight="bold">
        <RawMoney currency={money.currency}>{money.amount}</RawMoney>
      </Heading>
    </Desktop>
    <Mobile>
      <Body weight="bold">
        <RawMoney currency={money.currency}>{money.amount}</RawMoney>
      </Body>
    </Mobile>
  </Box>
);

export const DaysKey = ({ days }) => {
  return <Body inline>{days}</Body>;
};

export const PriceFreezeDateKey = ({ expirationDate }) => {
  return (
    <PriceFreezeDate
      format="ddMMMHH"
      date={toISoSOffsetString(expirationDate)}
    />
  );
};

export const PriceFreezeTimeKey = ({ expirationDate }) => {
  return (
    <PriceFreezeDate format="HHmm" date={toISoSOffsetString(expirationDate)} />
  );
};

export const BoldBookingIdStyle = ({ bookingId }) => {
  return (
    <Body inline weight="bold" color="primary.90">
      {bookingId}
    </Body>
  );
};

export const BoldKeyStyle = ({ t }) => {
  return (
    <Body inline type="small" weight="bold">
      {t('pricefreeze.downloadapp.subtitle.bold')}
    </Body>
  );
};

export const RegularKeyStyle = ({ t }) => {
  return <>{t('pricefreeze.downloadapp.subtitle.bold')}</>;
};

export const DownloadAppTitle = ({ children }) => {
  return (
    <>
      <Desktop>
        <Body type="base" weight="medium" color="neutral.90">
          {children}
        </Body>
      </Desktop>
      <Mobile>
        <Body type="base" weight="medium" color="neutral.90">
          {children}
        </Body>
      </Mobile>
    </>
  );
};

export const DownloadAppSubtitle = ({ t, subtitle }) => {
  return (
    <>
      <Desktop>
        <Heading type="5" weight="normal" color="neutral.90">
          <Trans
            i18nKey={subtitle}
            components={{
              boldKey: <RegularKeyStyle t={t} />,
            }}
          />
        </Heading>
      </Desktop>
      <Mobile>
        <Body type="small" weight="normal" color="neutral.90">
          <Trans
            i18nKey={subtitle}
            components={{
              boldKey: <BoldKeyStyle t={t} />,
            }}
          />
        </Body>
      </Mobile>
    </>
  );
};

export const TermsConditionsLinkText = ({ children }) => {
  return (
    <>
      <Desktop>
        <Body inline type="base" weight="regular">
          {children}
        </Body>
      </Desktop>
      <Mobile>
        <Body inline type="xSmall" weight="regular">
          {children}
        </Body>
      </Mobile>
    </>
  );
};

function _CheckChronologyTittle({ days, expirationDate }) {
  return (
    <Box pb="2x_big" maxWidth={['240px', 'none']}>
      <Box pb="2x_small">
        <Heading type="2" weight="bold" color="primary.90">
          <Trans
            i18nKey="pricefreeze.header.title.4"
            components={{
              daysKey: <DaysKey days={days} />,
            }}
          />
        </Heading>
      </Box>

      <Box>
        <Body type="base" color="primary.90">
          <Trans
            i18nKey="pricefreeze.confirmation.step1.description"
            components={{
              timeKey: <PriceFreezeTimeKey expirationDate={expirationDate} />,
              dateKey: <PriceFreezeDateKey expirationDate={expirationDate} />,
            }}
          />
        </Body>
      </Box>
    </Box>
  );
}

export function CheckChronologyTittlePhase2({
  expirationDate,
  primeOfferSelected,
  days,
}) {
  const { t } = useTranslation();
  if (!primeOfferSelected) {
    return _CheckChronologyTittle({ days, expirationDate });
  }
  return (
    <Box pb="2x_big" maxWidth={['240px', 'none']}>
      <Box pb="2x_small">
        <Heading type="2" weight="bold" color="primary.90">
          {t('pricefreeze.confirmationpage.header.title')}
        </Heading>
      </Box>

      <Box>
        <Body type="base" color="primary.90">
          <Trans
            i18nKey="pricefreeze.confirmationpage.header.subtitle"
            components={{
              timeKey: <PriceFreezeTimeKey expirationDate={expirationDate} />,
              dateKey: <PriceFreezeDateKey expirationDate={expirationDate} />,
            }}
          />
        </Body>
      </Box>
    </Box>
  );
}

export const CheckChronologyTittle = createFeature({
  name: PRIME_FIRST_PHASE_2_MOBILE,
  variations: [
    _CheckChronologyTittle,
    CheckChronologyTittlePhase2,
    CheckChronologyTittlePhase2,
  ],
  version: 1,
});

export function ConfirmationStepOneSubtitleLegacy({ email, deposit }) {
  const { t } = useTranslation();
  return deposit.amount === 0 ? (
    <Body type="small" weight="normal" color="neutral.100">
      {t('pricefreeze.confirmation.step1.email', { email: email })}
    </Body>
  ) : (
    <Body type="small" weight="normal" color="neutral.100">
      {t('pricefreeze.confirmation.success.paid.trip.description', {
        mailKey: email,
      })}
    </Body>
  );
}

const ButtonWithTracking = withDatameshClick(Button, 'confirmation:button');

export function StepTwoLegacy({
  days,
  id,
  email,
  openTermsConditionsTracking,
  lastStep = false,
  deposit = {},
}) {
  const { t } = useTranslation();
  return (
    <NumberChronology number={2} last={lastStep}>
      <ChronologyContent
        heading={t('pricefreeze.confirmation.success.nextsteps.main', {
          daysKey: days,
        })}
        title={
          deposit.amount === 0
            ? t('pricefreeze.confirmationpage.book.trip')
            : t('pricefreeze.confirmation.success.nextsteps.title')
        }
      >
        <Box pt="big">
          <DownloadApp id={id} email={email} />

          <Box pb="1x_small">
            <Box as="span" onClick={openTermsConditionsTracking}>
              <TermsConditionsLinkText>
                <Link
                  href={t('terms.pricefreeze.url')}
                  target={'_blank'}
                  type={'dotted'}
                  inline
                >
                  {t('pricefreeze.moreinfo.termsandconditions.link')}
                </Link>
              </TermsConditionsLinkText>
            </Box>
          </Box>
        </Box>
      </ChronologyContent>
    </NumberChronology>
  );
}

function DownloadApp({ id, email }) {
  const { t } = useTranslation();
  const { track } = usePFTracking();
  const isWebView = useIsWebView();
  const openAppDeepLink = useOpenAppDeepLink(
    t('pricefreeze.card.appdownload.landing.url'),
    id,
    email
  );

  const downloadPageTracking = () => {
    track(ACTIONS.CONFIRMATION.CLICK.DOWNLOAD);
  };

  if (isWebView) {
    return null;
  }

  return (
    <Box pb="big">
      <Box pb="small">
        <DownloadAppTitle>
          {t('pricefreeze.downloadapp.trip.title')}
        </DownloadAppTitle>
      </Box>

      <Flex flexDirection="row" alignItems="center" justifyContent="left">
        <Link
          href={openAppDeepLink}
          target={'_blank'}
          isUnderlined={false}
          aria-label={'downloadAppButtonLink'}
        >
          <ButtonWithTracking
            type="primary"
            size="medium"
            aria-label="itinerary information"
            onClick={downloadPageTracking}
            sx={{
              width: '100%',
            }}
          >
            {t('pricefreeze.downloadapp.button')}
          </ButtonWithTracking>
        </Link>
      </Flex>
    </Box>
  );
}

const padNumber = (number) => String(number).padStart(2, '0');

const toISoSOffsetString = (expirationDate) => {
  if (!expirationDate) return null;

  const dateObj = new Date(Number(expirationDate));
  const month = padNumber(dateObj.getMonth() + 1);
  const day = padNumber(dateObj.getDate());
  const hours = padNumber(dateObj.getHours());
  const minutes = padNumber(dateObj.getMinutes());
  const seconds = padNumber(dateObj.getSeconds());
  const minutesOffset = padNumber(dateObj.getTimezoneOffset());
  const sign = minutesOffset > 0 ? '-' : '+';
  const hoursOffset = padNumber(Math.floor(Math.abs(minutesOffset) / 60));
  const remainingMinutes = padNumber(Math.abs(minutesOffset) % 60);
  return `${dateObj.getFullYear()}-${month}-${day}T${hours}:${minutes}:${seconds}${sign}${hoursOffset}:${remainingMinutes}`;
};
